import type { ContextualFieldInterface } from 'src/tpbp-configuration/mapper/mappingInterfaces'
import { FIELD_STATUSES } from 'src/tpbp-core/models/fields/fieldRules.const'
import { valueIsEmpty } from 'src/tpbp-core/utils/common.utils'
import type { PersonModel } from 'src/tpbp-json-api-handler/models/models'
import { getContextualFields } from 'src/utils/fieldRules/fieldRules.utils'
import type { ProxyRecord } from './deserializers/deserializers.types'

/**
 * Remove unwanted values from record, will mutate the given record
 */
export function cleanRecord(
  record: ProxyRecord,
  willSetDefaults = false,
  currentUser?: PersonModel,
): void {
  const fields = getContextualFields(record, currentUser)

  const doNotSetDefaults = !willSetDefaults
  for (const field of fields) {
    cleanRecordField(record, field, doNotSetDefaults)
  }
}

function cleanRecordField(
  record: ProxyRecord,
  field: ContextualFieldInterface,
  doNotSetDefaults: boolean,
): void {
  const currentValue = record[field.key]
  if (!field.status) {
    const keepCurrentValue =
      (doNotSetDefaults && valueIsEmpty(currentValue)) ||
      (!doNotSetDefaults && currentValue === undefined)
    record[field.key] = keepCurrentValue ? currentValue : null
    return
  }
  if (doNotSetDefaults) {
    return
  }

  if (
    field.enums?.length === 1 &&
    field.status === FIELD_STATUSES.isMandatory
  ) {
    const value = field.enums[0]
    const hasKey =
      value && typeof value === 'object' && Object.hasOwn(value, 'key')
    record[field.key] = hasKey ? (value as { key: unknown }).key : value
    return
  }

  const hasDefault =
    Object.hasOwn(field, 'default') || Object.hasOwn(field, 'dynamicDefault')
  if (!hasDefault) {
    return
  }
  const currentValueIsWrong =
    currentValue === null ||
    currentValue === undefined ||
    isValueOutOfEnum(field?.enums, currentValue)
  if (currentValueIsWrong) {
    const defaultValue = field.dynamicDefault
      ? field.dynamicDefault({ field, record })
      : field.default

    record[field.key] = defaultValue
  }
}

export function isValueOutOfEnum(enums, value): boolean {
  const enumsLength = enums?.length ?? 0
  if (enumsLength === 0) {
    return false
  }
  const index = enums.findIndex((enumRow) => {
    const enumValue =
      enumRow !== null && typeof enumRow === 'object' ? enumRow.key : enumRow
    return value === enumValue
  })
  return index < 0
}
